import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Copy, Check, ArrowLeft, Play, Loader2, Youtube, AppWindow } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { NerdBadge } from "../components/NerdBadge";
import { EmailCapture } from "../components/EmailCapture";

const TikTokIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M16.5 3c.3 2.1 1.6 3.6 3.7 3.9v2.6c-1.3.1-2.5-.3-3.7-1v6.9c0 4.3-3.7 7-7.1 5.8-2.9-1-4.1-4.4-2.6-7 .9-1.6 2.6-2.5 4.4-2.4v2.7c-.4-.1-.9-.1-1.3 0-1.5.4-2 1.9-1.3 3.1.7 1.2 2.6 1.2 3.2-.3.1-.4.2-.8.2-1.2V3h4.1z" />
  </svg>
);

function VersionBlock({ version, index }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(version.prompt || "");
      setCopied(true);
      toast.success("Prompt copied ✓");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      try {
        const ta = document.createElement("textarea");
        ta.value = version.prompt || "";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopied(true);
        toast.success("Prompt copied ✓");
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error("Couldn't copy — please select and copy manually.");
      }
    }
  };

  return (
    <div className="border-t border-navy/12 pt-12 first:border-t-0 first:pt-0" data-testid={`version-block-${index}`}>
      <div className="flex flex-wrap items-center gap-4">
        <NerdBadge difficulty={version.difficulty} />
        <h2 className="text-3xl md:text-4xl">{version.difficulty} version</h2>
      </div>

      <div className="mt-6 grid gap-6 md:grid-cols-[220px_1fr]">
        {version.image ? (
          <img src={version.image} alt={`${version.difficulty} app`} className="w-full rounded-card border border-navy/12 object-cover shadow-card md:aspect-square" data-testid={`version-image-${index}`} />
        ) : (
          <div className="grid aspect-square w-full place-items-center rounded-card border border-dashed border-navy/25 bg-paper text-text-label" data-testid={`version-image-placeholder-${index}`}>
            <div className="flex flex-col items-center gap-2">
              <AppWindow size={26} />
              <span className="font-sans text-[11px] font-bold uppercase tracking-[0.14em]">App preview</span>
            </div>
          </div>
        )}
        <div>
          {version.description && <p className="text-[17px] text-text-body">{version.description}</p>}
          {(version.tiktok_url || version.youtube_url) && (
            <div className="mt-4 flex flex-wrap gap-3">
              {version.youtube_url && (
                <a href={version.youtube_url} target="_blank" rel="noreferrer" className="btn btn-secondary !py-2 !px-4 text-[13px]" data-testid={`version-youtube-${index}`}>
                  <Youtube size={15} /> Watch on YouTube
                </a>
              )}
              {version.tiktok_url && (
                <a href={version.tiktok_url} target="_blank" rel="noreferrer" className="btn btn-secondary !py-2 !px-4 text-[13px]" data-testid={`version-tiktok-${index}`}>
                  <TikTokIcon size={14} /> Watch on TikTok
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {version.prompt && (
        <div className="mt-8" data-testid={`prompt-box-${index}`}>
          <div className="flex items-center justify-between rounded-t-card border border-b-0 border-navy bg-navy px-5 py-3">
            <span className="font-sans flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-on-navy-muted">
              <span className="flex gap-1.5">
                <span className="h-2.5 w-2.5 rounded-full bg-brick" />
                <span className="h-2.5 w-2.5 rounded-full bg-ochre-bright" />
                <span className="h-2.5 w-2.5 rounded-full bg-teal-bright" />
              </span>
              the prompt
            </span>
            <button onClick={copy} className="btn btn-primary !py-2 !px-4 text-[13px]" data-testid={`copy-prompt-btn-${index}`}>
              {copied ? <><Check size={15} /> Copied</> : <><Copy size={15} /> Copy prompt</>}
            </button>
          </div>
          <pre className="whitespace-pre-wrap rounded-b-card border border-t-0 border-navy bg-navy p-6 font-serif text-[15px] leading-relaxed text-bone" data-testid={`prompt-text-${index}`}>
{version.prompt}
          </pre>
        </div>
      )}

      {version.steps?.length > 0 && (
        <div className="mt-8" data-testid={`steps-${index}`}>
          <p className="eyebrow">How to use it</p>
          <ol className="mt-5 space-y-5">
            {version.steps.map((s, i) => (
              <li key={i} className="flex gap-4">
                <span className="font-sans grid h-9 w-9 shrink-0 place-items-center rounded-full bg-teal text-[15px] font-bold text-bone">{i + 1}</span>
                <span className="pt-1.5 text-[17px] text-text-body">{s}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export default function GuideDetail() {
  const { slug } = useParams();

  const { data: guide, isLoading, isError } = useQuery({
    queryKey: ["guide", slug],
    queryFn: () => api.get(`/guides/${slug}`).then((r) => r.data),
    retry: false,
  });

  useSeo({
    title: guide ? guide.seo_title || `${guide.title} — Practical AI for Everyday Life` : "Setup — Practical AI",
    description: guide?.seo_description || guide?.short_desc,
  });

  if (isLoading)
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="animate-spin text-teal" size={30} />
      </div>
    );

  if (isError || !guide)
    return (
      <div className="container-brand py-28 text-center">
        <h1 className="text-3xl">Setup not found</h1>
        <Link to="/setups" className="prose-link mt-4 inline-block">Back to the library</Link>
      </div>
    );

  // Use versions; fall back to legacy single prompt for older guides.
  const versions = (guide.versions && guide.versions.length)
    ? guide.versions
    : (guide.prompt
        ? [{ difficulty: guide.difficulty, image: "", description: "", prompt: guide.prompt, steps: guide.steps || [], tiktok_url: "", youtube_url: guide.video_url || "" }]
        : []);

  const tiers = [...new Set(versions.map((v) => v.difficulty))];

  return (
    <div>
      {/* HEADER */}
      <section className="bg-plexus grain relative overflow-hidden" data-testid="guide-hero">
        <div className="plexus-edge-corner absolute inset-0 opacity-30" />
        <div className="container-brand relative grid items-center gap-12 py-16 md:grid-cols-2 md:py-20">
          <div>
            <Link to="/setups" className="on-navy inline-flex items-center gap-2 font-sans text-[12px] font-bold uppercase tracking-[0.14em] text-on-navy-body no-underline hover:text-bone">
              <ArrowLeft size={14} /> All setups
            </Link>
            <p className="eyebrow mt-7 !text-on-navy-muted">{guide.category}</p>
            <h1 className="mt-3 max-w-xl text-4xl leading-[1.02] text-bone sm:text-5xl">{guide.title}</h1>
            <p className="mt-5 max-w-measure text-lg text-on-navy-body">{guide.short_desc}</p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <span className="font-sans inline-flex items-center rounded-full bg-ochre-bright px-4 py-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-navy">
                Comment "{guide.comment_word}"
              </span>
              {tiers.map((t) => <NerdBadge key={t} difficulty={t} />)}
            </div>
          </div>

          <div className="flex justify-center md:justify-end" data-testid="guide-header-media">
            <div className="w-full max-w-sm rounded-[22px] border border-white/10 bg-navy/40 p-2 shadow-hero backdrop-blur-sm">
              {guide.header_image ? (
                <img src={guide.header_image} alt={guide.title} className="aspect-[4/3] w-full rounded-[15px] object-cover" data-testid="guide-header-image" />
              ) : (
                <div className="grid aspect-[4/3] w-full place-items-center rounded-[15px] bg-navy/60 text-on-navy-muted">
                  <div className="flex flex-col items-center gap-2">
                    <AppWindow size={30} />
                    <span className="font-sans text-[11px] font-bold uppercase tracking-[0.16em]">App preview</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="sheet grain">
        <div className="container-brand grid gap-12 py-16 lg:grid-cols-[1.5fr_.85fr]">
          <div className="space-y-12">
            {versions.length === 0 ? (
              <p className="text-text-muted">This app's setups are coming soon.</p>
            ) : (
              versions.map((v, i) => <VersionBlock key={i} version={v} index={i} />)
            )}
          </div>

          <aside>
            <div className="brand-card sticky top-24 p-7">
              <p className="eyebrow">More like this</p>
              <h3 className="mt-2 text-2xl leading-tight">Get every new setup <span className="italic text-ochre">first.</span></h3>
              <div className="mt-4">
                <EmailCapture source={`guide-${guide.slug}`} heading="" sub="" compact redirectToThankYou />
              </div>
              <p className="mt-3 text-[13px] text-text-muted">No hype. Unsubscribe anytime.</p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
